import React, { ReactElement, useEffect, useRef, useState } from 'react';

import { useInternalEditor } from '../editor/useInternalEditor';
import { Account, DesignSettings, Node, SerializedNodes } from '../interfaces';
import { NodeElement } from '../nodes';
import { ROOT_NODE } from '../utils/constants';
import { useEditor } from '../hooks';
import { GlobalStyle } from './GlobalStyle';
import { ThemeProvider } from 'styled-components';

export type Frame = {
    data?: string | SerializedNodes;
    designSettings?: DesignSettings;
    account?: Account;
    config: {
        fontEndpoint: string;
    };
};

const RenderRootNode = ({ config }) => {
    const { timestamp, designSettings } = useInternalEditor((state, query) => ({
        timestamp: state.nodes[ROOT_NODE] && state.nodes[ROOT_NODE]._hydrationTimestamp,
        designSettings: query.getDesignSettings(),
    }));
    if (!timestamp) {
        return null;
    }

    return (
        <ThemeProvider theme={{ fontEndpoint: config.fontEndpoint, designSettings }}>
            <GlobalStyle />
            <NodeElement id={ROOT_NODE} key={timestamp} />
        </ThemeProvider>
    );
};

/**
 * A React Component that defines the editable area
 */
export const Frame: React.FC<React.PropsWithChildren<Frame>> = ({
    children,
    data,
    designSettings,
    account,
    config,
}) => {
    const { actions, query } = useEditor();
    const [firstInit, setFirstInit] = useState(false);

    const initialState = useRef({
        initialChildren: children,
        initialData: data,
    });

    useEffect(() => {
        if (!firstInit) {
            const { initialChildren, initialData } = initialState.current;
            if (initialData) {
                actions.history.ignore().deserialize(initialData, designSettings, account);
                setFirstInit(true);
            } else if (initialChildren) {
                const rootNode = React.Children.only(initialChildren) as React.ReactElement;
                const node = query
                    .parseReactElement(rootNode)
                    .toNodeTree(designSettings, account, (node: Node, jsx: ReactElement) => {
                        if (jsx === rootNode) {
                            node.id = ROOT_NODE;
                        }
                        return node;
                    });
                actions.history.ignore().addNodeTree(node, '');
                setFirstInit(true);
            }
        }
    }, [actions, query]);

    return <RenderRootNode config={config} />;
};
